import {announce} from '@github-ui/aria-live'
import {displayFlashMessage} from '@github-ui/code-scanning-shared/utils/display-flash-message'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {pageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useMutation, useQuery, useQueryClient} from '@github-ui/react-query'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useEffect, useRef} from 'react'

const StatusChecksResponseSchema = Type.Object({
  statusRollup: Type.Object({
    pendingWorkflowApprovalRollup: Type.Union([
      Type.Object({
        viewerCanApproveWorkflowRuns: Type.Boolean(),
        hasExpiredWorkflowRuns: Type.Boolean(),
        approvalRequiredMessage: Type.String(),
      }),
      Type.Null(),
    ]),
  }),
})

type StatusChecksResponse = Type.Static<typeof StatusChecksResponseSchema>

type PendingWorkflowApprovalRollup = StatusChecksResponse['statusRollup']['pendingWorkflowApprovalRollup']

const StatusChecksResponseValidator = Compile(StatusChecksResponseSchema)

export interface WorkflowApproval {
  /** True when the viewer can approve the pending runs, so the approve action replaces the Copilot button. */
  canApprove: boolean
  /** Guidance shown alongside the Copilot state; null while `canApprove` is true. */
  message: string | null
  approve: () => void
  isApproving: boolean
}

function deriveApprovalState(rollup: PendingWorkflowApprovalRollup | undefined): {
  canApprove: boolean
  message: string | null
  announcement: string | null
} {
  if (!rollup) return {canApprove: false, message: null, announcement: null}

  // Expired runs cannot be re-requested, so the approve action is withheld even for permitted viewers.
  if (rollup.hasExpiredWorkflowRuns) {
    const message = 'Unable to re-run one or more workflows because they were created over a month ago.'
    return {canApprove: false, message, announcement: message}
  }

  if (rollup.viewerCanApproveWorkflowRuns) {
    return {
      canApprove: true,
      message: null,
      // The approve action replaces the button's label rather than mounting text, which AT does not reliably announce.
      announcement: 'Workflows are awaiting approval. Approve workflows to run.',
    }
  }

  const message = 'A maintainer must approve the workflows to verify this fix.'
  return {canApprove: false, message, announcement: message}
}

export function useWorkflowApproval({
  basePageDataUrl,
  enabled,
}: {
  basePageDataUrl: string
  enabled: boolean
}): WorkflowApproval {
  const statusChecksUrl = pageDataUrl(basePageDataUrl, PageData.statusChecks)
  const queryClient = useQueryClient()
  const queryKey = ['assign-to-copilot-workflow-approval', statusChecksUrl]
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Permission state must refetch rather than persist; TypeBox validates this JSON endpoint because it has no protobuf schema.
  const {data: statusChecksData} = useQuery({
    queryKey,
    queryFn: async ({signal}): Promise<StatusChecksResponse> => {
      const response = await reactFetchJSON(statusChecksUrl, {signal})
      if (!response.ok) throw new Error('Failed to fetch workflow approval status')
      const body = (await response.json()) as unknown
      if (!StatusChecksResponseValidator.Check(body)) throw new Error('Malformed workflow approval status response')
      return body
    },
    enabled,
    throwOnError: false,
  })

  const {mutate, isPending: isApproving} = useMutation({
    mutationFn: async () => {
      const response = await reactFetchJSON(pageDataUrl(basePageDataUrl, PageData.runActionRequiredWorkflows), {
        method: 'POST',
      })
      if (!response.ok) throw new Error('Failed to approve workflows')
    },
    onSuccess: () => {
      announce('Successfully approved and re-requested workflows.')
      return queryClient.invalidateQueries({queryKey})
    },
    onError: () => {
      displayFlashMessage('error', 'Failed to approve workflows, please try again.')
    },
  })

  const {canApprove, message, announcement} = deriveApprovalState(
    statusChecksData?.statusRollup.pendingWorkflowApprovalRollup,
  )

  const announcedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!announcement || announcedRef.current === announcement) return

    announcedRef.current = announcement
    announce(announcement)
  }, [announcement])

  return {canApprove, message, approve: () => mutate(), isApproving}
}
